import type { ReactNode } from "react";

/**
 * Markdown minimale zero-dep per le risposte di Aida: grassetto, corsivo,
 * codice inline, heading, elenchi puntati e tabelle GitHub-style. Condiviso tra
 * la chat dashboard e la chat di preparazione.
 */
export function MiniMarkdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = (key: string) => {
    if (bullets.length) {
      const items = bullets;
      bullets = [];
      blocks.push(
        <ul key={key} className="my-1 ml-4 list-disc space-y-0.5">
          {items.map((b, i) => (
            <li key={i}>{renderInline(b)}</li>
          ))}
        </ul>,
      );
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Tabella: riga con pipe seguita (dopo eventuali vuote) da un separatore |---|.
    if (isPipeRow(line)) {
      const gruppo: string[] = [];
      let j = i;
      while (j < lines.length) {
        if (isPipeRow(lines[j])) {
          gruppo.push(lines[j]);
          j++;
        } else if (lines[j].trim() === "" && j + 1 < lines.length && isPipeRow(lines[j + 1])) {
          j++; // salta una riga vuota tra le righe della tabella
        } else {
          break;
        }
      }
      if (gruppo.length >= 2 && isSeparatorRow(gruppo[1])) {
        flushBullets(`ul-${i}`);
        blocks.push(<Tabella key={`tb-${i}`} righe={gruppo} />);
        i = j;
        continue;
      }
    }

    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushBullets(`ul-${i}`);
      blocks.push(
        <p key={i} className="mb-0.5 mt-1 font-semibold">
          {renderInline(h[2])}
        </p>,
      );
      i++;
      continue;
    }

    const m = line.match(/^\s*[-*]\s+(.*)$/);
    if (m) {
      bullets.push(m[1]);
      i++;
      continue;
    }
    flushBullets(`ul-${i}`);
    if (line.trim() !== "") {
      blocks.push(
        <p key={i} className="my-0.5">
          {renderInline(line)}
        </p>,
      );
    }
    i++;
  }
  flushBullets("ul-end");
  return <>{blocks}</>;
}

function isPipeRow(line: string): boolean {
  return line.trim().length > 0 && line.includes("|");
}

function isSeparatorRow(line: string): boolean {
  return celle(line).every((c) => /^:?-{1,}:?$/.test(c.trim()));
}

/** Celle di una riga markdown table, senza i bordi pipe vuoti. */
function celle(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

function Tabella({ righe }: { righe: string[] }) {
  const header = celle(righe[0]);
  const corpo = righe.slice(2).map(celle);
  return (
    <div className="my-1 overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} className="border-b px-2 py-1 text-left font-semibold">
                {renderInline(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {corpo.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} className="border-b border-border/50 px-2 py-1 align-top">
                  {renderInline(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`)/g;
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      nodes.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      nodes.push(
        <code key={k++} className="rounded bg-muted px-1 text-[0.85em]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else {
      nodes.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
